import { trackEvent } from "../utils/cropRatioValidation";

export const TOO_LOW_DPI = 90;

let testGroup = null;
let exposureTracked = false;

export const upscaleTestGroup = () => testGroup?.name ?? null;

// Omitted rather than null: a null would override the persisted super
// property on later page loads (e.g. after an upscale reloads the page).
export const upscaleTestGroupProps = () =>
  testGroup ? { upscale_test_group: testGroup.name } : {};

// Without a test key every eligible customer sees the offer. With one, only
// a resolved treatment group does, so control never sees a flash of it.
export const upscaleTreatmentActive = (testKey, group = testGroup) =>
  !testKey || (!!group && !group.isControl);

export const UPSCALE_TEST_CART_ATTRIBUTE = "_upscale_test";
const CONTROL_SHARE = 0.5;

// 32-bit FNV-1a, mapped to [0, 1).
export const hashToUnit = (value) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 2 ** 32;
};

export const upscaleTestAssignment = (testKey, deviceId) => {
  if (!testKey || !deviceId) return null;
  const isControl = hashToUnit(`${testKey}:${deviceId}`) < CONTROL_SHARE;
  return { name: isControl ? "control" : "treatment", isControl };
};

// The server decides eligibility (Upscaling::Eligibility); this only guards
// against stale props for an image that has since been upscaled.
export const upscaleEligible = (image, upscaleAvailable) =>
  upscaleAvailable === "available" && !image?.ai_upscaled;

export const MM_PER_INCH = 25.4;

export const inchesToMm = (inches) =>
  Number(inches) > 0 ? Math.round(Number(inches) * MM_PER_INCH) : null;

export const maxLongestMmOk = (image) => {
  const longest = Math.max(image?.width || 0, image?.height || 0);
  return longest ? Math.floor((longest / TOO_LOW_DPI) * MM_PER_INCH) : null;
};

const A_SIZE_SHORT_MM = [
  ["A0", 841],
  ["A1", 594],
  ["A2", 420],
  ["A3", 297],
  ["A4", 210],
  ["A5", 148],
];

export const largestAvailableASize = (shortestPrintLength) => {
  const inches = Number(shortestPrintLength);
  if (!(inches > 0)) return null;
  const match = A_SIZE_SHORT_MM.find(([, mm]) => mm / MM_PER_INCH <= inches);
  return match ? match[0] : null;
};

export const upscaleEventContext = (image, upscaleAvailable) => {
  const offerAvailable = upscaleEligible(image, upscaleAvailable);
  return {
    image_id: image?.id,
    ai_upscaled: !!image?.ai_upscaled,
    upscale_eligible: offerAvailable || !!image?.ai_upscaled,
    upscale_offer_available: offerAvailable,
    ...upscaleTestGroupProps(),
  };
};

// No device ID means no group, which keeps the offer hidden.
const whenDeviceId = (callback, attempts = 20) => {
  const deviceId = window.mixpanel?.get_property?.("$device_id");
  if (deviceId) return callback(deviceId);
  if (attempts > 0) setTimeout(() => whenDeviceId(callback, attempts - 1), 500);
};

const saveCartAttribute = (value) => {
  if (typeof window.fetch !== "function") return;
  window
    .fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { [UPSCALE_TEST_CART_ATTRIBUTE]: value } }),
    })
    .catch(() => {});
};

const registerSuperProperties = (properties, attempts = 20) => {
  if (typeof window.mixpanel?.register === "function") {
    window.mixpanel.register(properties);
  } else if (attempts > 0) {
    setTimeout(() => registerSuperProperties(properties, attempts - 1), 500);
  }
};

const assignGroup = (testKey, deviceId) => {
  if (!testGroup) {
    testGroup = upscaleTestAssignment(testKey, deviceId);
    registerSuperProperties({
      upscale_test_group: testGroup.name,
      upscale_test_is_control: testGroup.isControl,
    });
  }
  return testGroup;
};

// Runs on every page load: the assignment is deterministic, and the
// persisted super property does not reliably survive the reload that
// follows an accepted upscale.
export const resolveUpscaleTestGroup = ({ testKey, onGroup }) => {
  if (!testKey) return;
  whenDeviceId((deviceId) => onGroup?.(assignGroup(testKey, deviceId)));
};

export const trackUpscaleExposure = ({ imageId, testKey, onGroup }) => {
  if (!testKey) return;
  whenDeviceId((deviceId) => {
    const group = assignGroup(testKey, deviceId);
    onGroup?.(group);
    if (exposureTracked) return;
    exposureTracked = true;
    trackEvent("A/B Test", {
      experiment_id: testKey,
      experiment: "upscale",
      device_id: deviceId,
      country: window.App?.currentCountry ?? null,
      test_group_is_control: group.isControl,
      test_group_name: group.name,
      image_id: imageId,
    });
    saveCartAttribute(`${testKey}:${group.name}`);
  });
};

export const resetUpscaleTrackingForTests = () => {
  testGroup = null;
  exposureTracked = false;
};
